import parseInt from 'lodash/parseInt'
import { Map, List } from 'immutable'
import { join as joinPromise } from 'bluebird'
import { getConfigDataServer } from '../config'
import { isLoggedIn } from '../auth'
import { getUserList } from '../user'
import { getNodeList } from '../node'
import { isPageReady } from '../pages'
import { getLocationPathname } from '../location'
import { SET_AUTH_DATA } from '../auth/redux/actions'
import { SET_LOCATION_DATA } from '../location/redux/actions'
import { SET_PAGE_READY } from '../pages/redux/actions'
import {
  GET_COMMENTS,
  SET_COMMENTS_DATA_ITEM_STATUS,
  setComments as setCommentsAction,
  getComments as getCommentsAction,
  setCommentsOffsetLimit as setCommentsOffsetLimitAction,
  setCommentsDataItemPublishing as setCommentsDataItemPublishingAction,
  deleteCommentsDataItemPublishing as deleteCommentsDataItemPublishingAction,
} from './redux/actions'
import { update, getCommentsList, STATUS_PUBLISHED } from '.'

const log = console

/**
 * Handle the page ready event at this point we have auth and config
 */
export function pageReadyHandler ({ after }) {
  return after(
    [SET_AUTH_DATA, SET_LOCATION_DATA, SET_PAGE_READY],
    async ({ dispatch, state } = {}) => {
      const { comments } = state
      if (comments.get('data') || comments.get('processing')) {
        return
      }
      dispatch(getCommentsAction({ storeKey: 'commentsList' }))
    },
  ).when(({ state }) => {
    const { location, auth, page } = state
    return getLocationPathname(location) === '/comments/list' && isLoggedIn(auth) && isPageReady({ page })
  })
}

/**
 * Handle getting comments
 */
export function getCommentsHandler ({ after }) {
  return after(GET_COMMENTS, async ({ dispatch, state, action } = {}) => {
    const {
      config, auth, comments, location,
    } = state
    const {
      storeKey,
      offset = parseInt(location.getIn(['data', 'query', 'offset'], 0)),
      limit = parseInt(location.getIn(['data', 'query', 'limit'], 100)),
    } = action.payload
    const processing = comments.getIn([storeKey, 'processing'])
    const query = location.getIn(['data', 'query'])
    if (processing || comments.getIn([storeKey, 'offset']) === offset) {
      return
    }
    try {
      dispatch(setCommentsOffsetLimitAction({ storeKey, offset, limit }))
      const commentsList = await getCommentsList({
        auth,
        server: getConfigDataServer(config),
        uid: query.get('uid'),
        contentId: query.get('contentId'),
        status: query.get('status'),
        offset,
        limit,
      })
      let commentData = commentsList.get('comments') || List()
      if (!commentData.isEmpty()) {
        const [userList, nodeList] = await joinPromise(
          getUserList({
            uid: commentData.reduce((reduction, comment) => {
              const uid = comment.get('uid')
              if (!uid || reduction.includes(uid)) {
                return reduction
              }
              return reduction.concat(uid)
            }, List()),
            auth,
            server: getConfigDataServer(config, 'auth'),
          }),
          getNodeList({
            nids: commentData.map((comment) => comment.get('contentId')),
            server: getConfigDataServer(config),
          }),
        )
        // Add users to comments
        commentData = commentData.map((comment) => {
          const user = userList.find((u) => parseInt(u.get('uid')) === parseInt(comment.get('uid')))
          const node = nodeList.find((n) => parseInt(n.get('nid')) === parseInt(comment.get('contentId')))
          let commentHydrated = comment
          if (user) {
            commentHydrated = comment.set('user', user)
          }
          if (node) {
            commentHydrated = commentHydrated.set('content', Map({
              title: node.get('title'),
              path: node.get('path'),
            }))
          }
          return commentHydrated
        })
      }
      dispatch(setCommentsAction({
        storeKey,
        data: commentData,
        total: commentsList.get('total'),
      }))
    } catch (error) {
      log.error(error)
    }
  })
}

export function setCommentsDataItemStatusHandler ({ after }) {
  return after(SET_COMMENTS_DATA_ITEM_STATUS, async ({ dispatch, state, action } = {}) => {
    const { config, auth, comments } = state
    const { storeKey, item, status } = action.payload
    const comment = comments.getIn([storeKey, 'data'], List()).find((dataItem) => dataItem.get('id') === item.get('id'))
    if (!comment) {
      return
    }
    dispatch(setCommentsDataItemPublishingAction({
      storeKey,
      item,
      publishing: status === STATUS_PUBLISHED,
    }))
    await update({
      auth,
      server: getConfigDataServer(config),
      id: comment.get('id'),
      data: Map({
        subject: comment.get('subject'),
        comment: comment.get('comment'),
        status,
      }),
    })
    dispatch(deleteCommentsDataItemPublishingAction({ storeKey, item }))
  })
}
